import type { Grant, RefreshAttempt, Session } from "./auth.ts";
import type { Ask } from "./ask.ts";
import { leaveByChoice, type ChosenLeavePage } from "./leave-flow.ts";
import type { Choosing } from "./login.tsx";
import { changeQuestion } from "./menu/wording.ts";

/**
 * The game menu's Change character as the page runs it (P1.9-T62; ADR-065's note "Change character and the release
 * of the character"): leaving the world for character select without logging out or reloading. The flow is the
 * page's order made explicit, so it is unit-tested without a page; `main.ts` gives it the page's parts. The way back
 * to select once the player has decided (`returnToSelect`) is also Leave queue's (`queue/leave-queue.ts`).
 */

/**
 * What returning to character select needs from the page.
 */
export interface SelectReturnPage {
  /** The account session the page plays, or null for a `?token=` dev session, which has no select to return to. */
  readonly current: () => Session | null;
  /**
   * Departs the world: stops keeping the session fresh, forgets the tab's grant, so a reload while select shows
   * meets the log in form as one after a login does, releases the character, settling once the gateway has taken it
   * out of the world or the bound has passed (`GameScene.depart`), and forgets what the chat held for it
   * (`ChatStore.forget`), so no line, whisperer or block list reaches the next character or account.
   */
  readonly depart: () => Promise<void>;
  /** Refreshes the kept session, naming the character it played; never rejects. */
  readonly renew: (refreshToken: string, characterId: string | undefined) => Promise<RefreshAttempt>;
  /** Shows character select for the account and settles with the session the player goes on with. */
  readonly choose: (choosing: Choosing) => Promise<Session>;
  /** Plays a session: stores it, keeps it fresh, names the HUD and joins the world with it. */
  readonly play: (session: Session) => void;
  /** Stores a grant, or none, and reloads the page. */
  readonly reload: (grant: Grant | null) => void;
}

/**
 * What Change character needs from the page: the way back to select, the confirm dialog it asks in first, and the
 * chosen leave the gateway must take before the page goes (`leave-flow.ts`).
 */
export interface ChangeCharacterPage extends SelectReturnPage, ChosenLeavePage {
  /** The page's confirm dialog. */
  readonly ask: Ask;
}

/**
 * Changes character: asks first in the kit's confirm dialog; once the player confirms, asks the gateway to leave
 * (`leaveByChoice`), which a fight refuses and which otherwise waits out the character's stay, and then returns to
 * character select (`returnToSelect`).
 *
 * @param page What the flow needs from the page.
 * @returns True once the page has departed the world, false when nothing changed: the player cancelled, the page
 *   has no account session, or a fight refused the leave.
 */
export async function changeCharacter(page: ChangeCharacterPage): Promise<boolean> {
  if (page.current() === null) {
    return false;
  }
  if (!(await page.ask(changeQuestion()))) {
    return false;
  }
  if (!(await leaveByChoice(page))) {
    return false;
  }
  return returnToSelect(page);
}

/**
 * Returns to character select: departs the world, or the join the page was making, and waits for the gateway to
 * release the character, so the next join replaces nothing; refreshes the kept session for the account's characters
 * and its limit (P1.4-T6), and shows select from them; then plays the session chosen there, which may be the same
 * character, a new one, or, after Log out and a login on select, another account's. A refresh that gives no list
 * reloads the page with what it leads back to: nothing when the gateway refused the session, so the log in form; the
 * naming grant it gave, or the plain grant from a gateway before select; and the session the page played when the
 * gateway did not answer, so the page returns to the world.
 *
 * @param page What the flow needs from the page.
 * @returns True once the page has departed, false for a page with no account session, which has no select.
 */
export async function returnToSelect(page: SelectReturnPage): Promise<boolean> {
  const session = page.current();
  if (session === null) {
    return false;
  }
  await page.depart();
  const attempt = await page.renew(session.refreshToken, session.characterId);
  if (attempt.kind !== "granted") {
    page.reload(attempt.kind === "refused" ? null : { kind: "playing", session });
    return true;
  }
  const grant = attempt.grant;
  if (grant.kind !== "playing" || grant.roster === undefined) {
    page.reload(grant);
    return true;
  }
  page.play(await page.choose({ refreshToken: grant.session.refreshToken, familyName: grant.session.familyName ?? "", roster: grant.roster }));
  return true;
}
